import React, { useState, useEffect } from 'react';
import { useMediaQuery } from 'react-responsive';
import Helmet from 'react-helmet';

import CabModifySearch from '../../components/CabModifySearch/CabModifySearch';
import WhyBookWithUs from '../../components/WhyBookWithUs/WhyBookWithUs';
import FaqSection from '../../components/Faq/Faq';
import RentalCabPrice from '../../components/RentalCabPrice/RentalCabPrice';
import RentalCabs from '../../components/RentalCabs/RentalCabs';
import ThingsToKnow from '../../components/ThingsToKnow/ThingsToKnow';
import TopCabsRoutes from '../../components/TopCabsRoutes/TopCabsRoutes';
import Footer from '../../components/Footer/Footer';
import BreadCrumb from '../../components/BreadCrumb/BreadCrumb';
import PageHeader from '../../components/PageHeader/PageHeader';
import CompactList from '../../components/CompactList/CompactList';
import { CAB_FROM_AIRPORT } from '../../constants/tripTypes';
import DataLoaderError from '../../../server/errors/DataLoaderError';
import AppContextProvider from '../../AppContext';
import { trackAirportTemplateEvent } from '../../../src/utils/omnitureTracking';
import './AirportTemplate.css';
import { EnvironmentVariables } from '../../types';
import { getAppEnv } from '../../../config/env';
import AirportCabList from './components/AirportCabList/AirportCabList';
import Overlay from '../../components/BPG/Overlay';
import { useKnowMoreDetails } from '../customHooks';
import AdsComponent from '../../components/adsComponent';
import { SEO_AIRPORT_TEMPLATE } from '../../constants/templateConstants';
import { yeildToMainThread } from '../../scheduler';
import {
  useSearchWidgetHooks,
  fetchAndLogXdmEvent
} from '../../utils/commonUtils';
import ProductSchema from '../../utils/ProductSchema';

const env = getAppEnv();
const { DATA_LOADER_URL } = env.raw as EnvironmentVariables;
const isSSR = typeof window === 'undefined';
const AirportTemplate = ({ data }) => {
  const isMobileDevice = useMediaQuery({
    query: '(max-device-width: 768px)'
  });
  const [
    selectedKnowMoreDetailsForMobile,
    setSelectedKnowMoreDetailsForMobile
  ] = useKnowMoreDetails(null, isMobileDevice);

  const delayedKnowMoreDetailsForMobile = async val => {
    await yeildToMainThread(() => setSelectedKnowMoreDetailsForMobile(val));
  };

  const {
    airportTerminalName: name,
    code,
    countryCode: airportCountryCode,
    templateData,
    topRoutesfromCity,
    cabTypesAvailableForRent,
    cityDetails,
    cabRentalPrices,
    airportCabs,
    otherCitiesData,
    hrPackages,
    interlinks,
    aggregateRating,
    schemaMinFare
  } = data;

  const { name: cityName } = cityDetails;
  const { travelFor, pickUpTime, dropTime } = useSearchWidgetHooks(
    SEO_AIRPORT_TEMPLATE
  );

  const [isOverlayVisible, setIsOverlayVisible] = useState(false);

  const toggleOverlay = (isVisible: boolean) => {
    setIsOverlayVisible(isVisible);
  };

  useEffect(() => {
    const url = typeof window !== 'undefined' ? window.location.href : data.url;
    fetchAndLogXdmEvent(url);
  }, []);

  return (
    <AppContextProvider
      omniUrlArgs={`source=${SEO_AIRPORT_TEMPLATE}`}
      trackTemplateEvent={trackAirportTemplateEvent}
      selectedKnowMoreDetailsForMobile={selectedKnowMoreDetailsForMobile}
      setSelectedKnowMoreDetailsForMobile={delayedKnowMoreDetailsForMobile}
    >
      <div
        className={`cabSeoDtPage ${
          selectedKnowMoreDetailsForMobile ? 'expanded' : ''
        }`}
      >
        <Helmet>
          <title>{templateData.metaTitle}</title>
          <meta name="description" content={templateData.metaDescription} />
          <meta name="keywords" content={templateData.metaKeywords} />
          <link rel="canonical" href={templateData.sampleUrl} />
          {templateData.ogTitle && (
            <meta property="og:title" content={templateData.ogTitle} />
          )}
          {templateData.ogDescription && (
            <meta
              property="og:description"
              content={templateData.ogDescription}
            />
          )}
          {templateData.ogImage && (
            <meta name="og:image" content={templateData.ogImage} />
          )}
          {templateData.twitterTitle && (
            <meta name="twitter:title" content={templateData.twitterTitle} />
          )}
          {templateData.twitterDescription && (
            <meta
              name="twitter:description"
              content={templateData.twitterDescription}
            />
          )}
          {templateData.twitterImage && (
            <meta name="twitter:image" content={templateData.twitterImage} />
          )}
        </Helmet>
        {aggregateRating && (
          <ProductSchema
            data={aggregateRating}
            templateData={templateData}
            schemaMinFare={schemaMinFare}
            source={name}
          />
        )}
        <div className={`domChanger mobileShow makeSticky`}>
          <CabModifySearch
            isOverlayVisible={isOverlayVisible}
            overlayToggle={toggleOverlay}
            airportCountryCode={airportCountryCode}
            pickupTime={pickUpTime}
            defaultTripType={travelFor}
            returnTime={dropTime}
            sourceCityCode={code}
            sourceCityName={name}
            isMobileDevice={true}
            title={`${name} Cabs`}
            hrPackages={hrPackages}
          />
        </div>

        <div className={`desktopShow makeSticky`}>
          <CabModifySearch
            airportCountryCode={airportCountryCode}
            pickupTime={pickUpTime}
            defaultTripType={travelFor}
            returnTime={dropTime}
            sourceCityCode={code}
            sourceCityName={name}
            isMobileDevice={false}
            title={`${name} Cabs`}
            hrPackages={hrPackages}
          />
        </div>
        <main className="container">
          <BreadCrumb breadcrumb={templateData.breadcrumb} />
          <PageHeader
            title={templateData.h1Title}
            extraInfo={templateData.extraInfo}
            isMobileDevice={isMobileDevice}
          />

          <div className="appendBottom20">
            {/* {
              <AdsComponent
                template={'Airport'}
                placedAt={'top'}
                isMobileDevice={isMobileDevice}
                desktopAdConfig={{
                  position: templateData.bannerDesktop
                    ? templateData.bannerDesktop
                    : 'none',
                  class: 'desktopShow appendBottom20 adsLayout',
                  style: {}
                }}
                mobileAdConfig={{
                  position: templateData.bannerMobile
                    ? templateData.bannerMobile
                    : 'none',
                  class: 'mobileShow appendBottom20 adsLayout',
                  style: { marginTop: null }
                }}
              />
            } */}
            {/* <div className="appendBottom20">
              <EPassTile html={mySafetyHtmls.infoMessage} />
            </div> */}
            {/* {mySafetyHtmls.mySafetyUsp && (
              <div className="appendBottom20">
                <MySafetyBanner html={mySafetyHtmls.mySafetyUsp} />
              </div>
            )} */}
          </div>
          {airportCabs.status === 'SUCCESS' &&
            airportCabs.airport_drop_cab_list &&
            airportCabs.airport_drop_cab_list.length > 0 &&
            airportCabs.airport_pickup_cab_list &&
            airportCabs.airport_pickup_cab_list.length > 0 && (
              <section className="">
                {/* <h2 className="latoRegular">
                Distance{' '}
                <span className="latoBold">
                  {airportCabs.oneway_distance_in_kms} kms
                </span>{' '}
                | Duration{' '}
                <span className="latoBold">
                  {formatDuration(airportCabs.oneway_duration_in_minutes)}
                </span>
              </h2> */}
                <AirportCabList
                  data={airportCabs}
                  cityName={cityName}
                  airportName={name}
                />
              </section>
            )}
          {topRoutesfromCity.cabTypes.length > 0 && (
            <TopCabsRoutes
              h2={`Top Cabs Routes from ${cityDetails.name} Airport`}
              cityName={cityDetails.name}
              routeType="From"
              data={topRoutesfromCity}
              enableAnchorTags={true}
              reduceCssCalculations={false}
            />
          )}
          <section className="bookWithUsWrap visibilityAuto">
            <h2>Why book with Us</h2>
            <WhyBookWithUs usps={templateData.usps} />
          </section>
          {cabTypesAvailableForRent &&
            cabTypesAvailableForRent.packages &&
            cabTypesAvailableForRent.packages.length > 0 && (
              <RentalCabs
                h2={`Rental cabs in ${cityDetails.name} Airport`}
                cityName={cityDetails.name}
                data={cabTypesAvailableForRent}
              />
            )}
          {/* {mySafetyHtmls.ePassUsp && (
            <div className=" appendBottom40">
              <EpassBnr html={mySafetyHtmls.ePassUsp} />
            </div>
          )}
          <br style={{ display: 'none' }} /> */}

          {/*https://github.com/contra/react-responsive/issues/162*/}
          {/* {mySafetyHtmls.stateGuidelines && (
            <section>
              <h2>State Guidelines on e-Passes</h2>
              <StateGuidelines html={mySafetyHtmls.stateGuidelines} />
            </section>
          )} */}
          {templateData.descriptionInHtml &&
          topRoutesfromCity.cabTypes.length > 0 ? (
            <section id="descriptionInHtml" className="visibilityAuto">
              <ThingsToKnow html={templateData.descriptionInHtml} />
            </section>
          ) : null}
          {cityDetails.description2HTML && (
            <section className="visibilityAuto">
              <h2>{`${cityDetails.name} Cab Booking on MMT`}</h2>
              <ThingsToKnow html={cityDetails.description2HTML} />
            </section>
          )}
          {cityDetails.description && (
            <section className="visibilityAuto">
              <h2>{`Things To Know About ${cityDetails.name}`}</h2>
              <ThingsToKnow text1={cityDetails.description} />
            </section>
          )}
          <div
            style={{ flexWrap: 'wrap' }}
            className={`cabsSeoWrap__Details makeFlex visibilityAuto`}
          >
            {interlinks &&
              interlinks.map(({ title, links }) => {
                return (
                  <CompactList
                    titlePrefix={title}
                    titleSuffix={``}
                    list={links}
                    type={'bullets'}
                    className={'rightMargin'}
                  >
                    {(item: any, idx, style) => (
                      <li
                        key={`${item.text}_${idx}`}
                        className={`${
                          idx ? `paddingT10 paddingB10` : `paddingB10`
                        }`}
                      >
                        {item.url ? (
                          <a
                            className={`linkBoxFont `}
                            onClick={() => {
                              //TO DO: add omniture events from Dataloader
                            }}
                            href={item.url}
                          >
                            {item.text}
                          </a>
                        ) : (
                          <span>{item.text}</span>
                        )}
                      </li>
                    )}
                  </CompactList>
                );
              })}
          </div>
          {templateData.faq &&
          Array.isArray(templateData.faq) &&
          templateData.faq.length > 0 ? (
            <section className="visibilityAuto">
              <h2>{`Frequently Asked Questions About ${name} Cab Booking`}</h2>
              <FaqSection
                trackTemplateEvent={trackAirportTemplateEvent}
                data={templateData.faq}
                pageUrl={templateData.sampleUrl}
              />
            </section>
          ) : null}
          {cabRentalPrices && cabRentalPrices.length > 0 ? (
            <section>
              <h2>{`${cityDetails.name} Rental Cabs Prices`}</h2>
              <RentalCabPrice cabRentalPrices={cabRentalPrices} />
            </section>
          ) : null}
          {otherCitiesData?.length > 0 && (
            <section className="visibilityAuto">
              <CompactList titlePrefix={`Other cities`} list={otherCitiesData}>
                {(item: any, idx, style) => (
                  <li key={`${item.name}_${idx}`} className="appendBottom10">
                    <a
                      href={item.url}
                      className={`font14 darkGreyText ${style.compactList__listLink}`}
                    >
                      {item['name']}
                    </a>
                  </li>
                )}
              </CompactList>
            </section>
          )}
        </main>
        {selectedKnowMoreDetailsForMobile ? (
          <Overlay
            cancelButton={() => delayedKnowMoreDetailsForMobile(null)}
            knowMoreDetails={selectedKnowMoreDetailsForMobile}
          />
        ) : null}
        <Footer template={templateData} />
        <div
          className="blackOverlay"
          onClick={() => {
            delayedKnowMoreDetailsForMobile(null);
          }}
        ></div>
        {isOverlayVisible && (
          <div
            onClick={() => {
              toggleOverlay(false);
            }}
            className="searchWidgetModal mobileShow"
          ></div>
        )}
      </div>
    </AppContextProvider>
  );
};

AirportTemplate.fetchData = async (_, match, urlObj) => {
  const {
    params: { airportCode }
  } = match;
  const url = `${DATA_LOADER_URL}/api/airportTemplateData/${airportCode}?${urlObj.query}`;
  let responseData;

  const response = await fetch(url);
  const textResponse = await response.text();
  try {
    responseData = JSON.parse(textResponse);
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('url', url);
    throw new Error('JSON parse exception: ' + textResponse);
  }
  if (responseData.status !== 'SUCCESS') {
    // eslint-disable-next-line no-console
    console.error('url', url);
    throw new DataLoaderError(
      `Unable to fetch city details of ${airportCode}`,
      responseData.error
    );
  }
  return responseData;
};
export default AirportTemplate;
